<template>
    <ContentComp class="h-100">
        <MessageContainer
            v-if="!ListenForGlobalStatuses"
            :show-all-messages="true"
        />
        <ToolbarComp
            :search-box-placeholder="$tm('devicestatus.searchplaceholder')"
            :searchtext="_searchText"
            @searching="onSearching"
        >
            <template #first>
                <ButtonGroup :actions="toolbarActions" />
                <InputDropdownCheckboxes
                    v-model="selectedDeviceTypes"
                    :data="devicetypesDatasource"
                    :icon="featureIcons.DEVICE"
                    ><span class="ms-2">{{ $tm('devicestatus.devicetypes') }}</span></InputDropdownCheckboxes
                >
            </template>
        </ToolbarComp>
        <template v-if="isGroupedList">
            <div class="h-100 overflow-auto">
                <div
                    v-for="(row, i) in rows"
                    :key="i"
                    class="flex-grow-1"
                >
                    <h3 v-if="(row[0].Station?.EventName || '') != ''">{{ row[0].Station?.EventName }}</h3>
                    <h3 v-else>{{ $tm('devicestatus.unknownevent') }}</h3>
                    <div class="flex-grow-1">
                        <DeviceStatusGrid :rows="row" />
                    </div>
                </div>
            </div>
        </template>
        <template v-else>
            <div
                v-for="(row, i) in rows"
                :key="i"
                class="flex-grow-1 overflow-auto"
            >
                <DeviceStatusGrid
                    ref="grid"
                    :rows="row"
                    :eventcolumn="true"
                />
            </div>
        </template>
    </ContentComp>
</template>
<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import ContentComp from '../layout/ContentComp.vue';
import DeviceStatusGrid from '../views/DeviceStatus/DeviceStatusGrid.vue';
import { useDeviceStatus } from '@/composeables/devicestatus/devicestatus';
import type { StationDeviceStatus } from '@/objects/generated';
import { $tm } from '@/locize';
import ToolbarComp from '../Toolbar/ToolbarComp.vue';
import { DropdownItemButton, type SelectLine, type SelectLineValueType } from '@/objects';
import { storeToRefs } from 'pinia';
import ButtonGroup from '../ButtonGroup/ButtonGroup.vue';
import InputDropdownCheckboxes from '../Basic/InputDropdownCheckboxes.vue';
import { featureIcons } from '../Menu/featureicons';
import { watch } from 'vue';
import MessageContainer from '../MessageContainer.vue';
import type { ComponentInstance } from 'vue';
import { DeviceTypeValues } from '@/objects/generated/enums/DeviceType';

const devicetypesDatasource = ref<Array<SelectLine>>([]);
const selectedDeviceTypes = ref<SelectLineValueType[]>([]);

// Ref to the grid
const grid = ref<ComponentInstance<typeof DeviceStatusGrid> | null>(null);

/**
 * A list of all the device statuses
 */
const { GlobalDeviceStatuses, ListenForGlobalStatuses, UpdatedDeviceIds } = storeToRefs(useDeviceStatus());

// This is the reactive search text that changes when the user types
// We dont want to use this one directly, but instead use the onSearching function
// This is only a dummy value
const _searchText = ref<string>('');

// This is the real search text that we use in the filter. This is updated when the user has stopped typing and pressed enter
const searchText = ref<string>('');

// Define the buttons we want in the toolbar
const toolbarActions = computed(() => {
    let actions: DropdownItemButton[] = [];

    // We need a button to enable global event listening
    if (!ListenForGlobalStatuses.value) {
        actions.push(
            DropdownItemButton.create({
                buttonType: 'Secondary',
                title: 'devicestatus.enableglobaleventlistening',
                icon: 'fa-bell-slash',
                class: 'active',
                action: () => {
                    ListenForGlobalStatuses.value = true;
                },
            }),
        );
    } else {
        actions.push(
            DropdownItemButton.create({
                buttonType: 'Secondary',
                title: 'devicestatus.disableglobaleventlistening',
                icon: 'fa-bell',
                class: 'active border-success bg-success-subtle',
                action: () => {
                    ListenForGlobalStatuses.value = false;
                },
            }),
        );
    }

    if (isGroupedList.value) {
        actions.push(
            DropdownItemButton.create({
                buttonType: 'Secondary',
                title: 'general.showaslist',
                icon: 'fa-list',
                action: () => {
                    isGroupedList.value = false;
                },
            }),
        );
    } else {
        actions.push(
            DropdownItemButton.create({
                buttonType: 'Secondary',
                title: 'general.showasgroupedlist',
                icon: 'fa-list-tree',
                action: () => {
                    isGroupedList.value = true;
                },
            }),
        );
    }

    // We need buttons to filter on online and offline devices
    actions.push(
        DropdownItemButton.create({
            buttonType: 'Secondary',
            title: 'devicestatus.showonline',
            icon: 'fa-signal-stream',
            class: showOnlineDevices.value ? 'active' : '',
            action: () => {
                showOnlineDevices.value = !showOnlineDevices.value;
            },
        }),
    );
    actions.push(
        DropdownItemButton.create({
            buttonType: 'Secondary',
            title: 'devicestatus.showoffline',
            icon: 'fa-signal-stream-slash',
            class: showOfflineDevices.value ? 'active' : '',
            action: () => {
                showOfflineDevices.value = !showOfflineDevices.value;
            },
        }),
    );

    // Hide devices with no event
    actions.push(
        DropdownItemButton.create({
            buttonType: 'Secondary',
            title: 'devicestatus.hidenodeviceevent',
            icon: 'fa-eye-slash',
            class: hideDevicesWithNoEvent.value ? 'active' : '',
            action: () => {
                hideDevicesWithNoEvent.value = !hideDevicesWithNoEvent.value;
            },
        }),
    );

    return actions;
});

// If we should show the grid as a grouped list or not
const isGroupedList = ref<boolean>(false);

// Filter for hiding online devices
const showOnlineDevices = ref<boolean>(true);

// Filter for hiding offline devices
const showOfflineDevices = ref<boolean>(true);

// Filter for hiding devices with no event connected
const hideDevicesWithNoEvent = ref<boolean>(false);

/**
 * The rows of the grid
 */
const rows = computed<Array<StationDeviceStatus[]>>(() => {
    let rows: Array<StationDeviceStatus[]> = [];
    GlobalDeviceStatuses.value?.forEach((s: StationDeviceStatus[]) => {
        rows.push(s);
    });
    rows.reverse(); // This make sure that the ones with an event is on top

    if ((!showOnlineDevices.value && !showOfflineDevices.value) || (showOnlineDevices.value && showOfflineDevices.value)) {
        // If both offline and online is checked or both are unchecked we show all
    } else if (showOnlineDevices.value) {
        rows = rows.map((r) => r.filter((s) => s.DeviceStatus?.Connected));
    } else if (showOfflineDevices.value) {
        rows = rows.map((r) => r.filter((s) => !s.DeviceStatus?.Connected));
    }

    if (hideDevicesWithNoEvent.value) {
        rows = rows.map((r) => r.filter((s) => s.Station?.EventName !== ''));
    }

    if (searchText.value !== '') {
        let searchTextLower = searchText.value.toLowerCase();
        rows = rows.map((r) =>
            r.filter(
                (s) =>
                    s.StationAlias?.AliasWithoutEtsChannel?.toLowerCase().includes(searchTextLower) ||
                    s.Station?.Name?.toLowerCase().includes(searchTextLower) ||
                    s.Station?.EventName?.toLowerCase().includes(searchTextLower),
            ),
        );
    }

    // Filter the device type
    if (selectedDeviceTypes.value.length > 0) {
        rows = rows.map((r) => r.filter((s) => selectedDeviceTypes.value.includes(s.DeviceStatus?.DeviceType!)));
    }

    // Remove empty rows
    rows = rows.filter((r) => r.length > 0);

    rows.sort((a, b) => {
        if (a[0].Station?.EventName == null || b[0].Station?.EventName == null) {
            return 0;
        }

        // We sort the ones with EventName empty last
        if (a[0].Station.EventName === '' && b[0].Station.EventName !== '') {
            return 1;
        }

        return a[0].Station.EventName.localeCompare(b[0].Station.EventName, 'nb');
    });

    if (!isGroupedList.value) {
        rows = [rows.flat()];
    }

    return rows;
});

/**
 * We use this one so we only filter when the user has stopped typing and pressed enter
 * @param search
 */
const onSearching = (search: string) => {
    searchText.value = search;
};

onMounted(() => {
    // Map deviceTypes to SelectLines
    let _deviceTypes: SelectLine[] = [];
    for (const key in DeviceTypeValues) {
        _deviceTypes.push({ text: `devicetype.${DeviceTypeValues[key]}`.toLocaleLowerCase(), value: DeviceTypeValues[key] });
    }

    _deviceTypes.sort((a, b) => $tm(a.text).localeCompare($tm(b.text)));

    devicetypesDatasource.value = _deviceTypes;
});
</script>
